import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Tag, Share2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/slug/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('Artykuł nie został znaleziony');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link skopiowany do schowka!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="animate-pulse">
            <div className="h-8 bg-navy/10 rounded w-1/4 mb-8"></div>
            <div className="h-12 bg-navy/10 rounded w-3/4 mb-4"></div>
            <div className="h-6 bg-navy/10 rounded w-1/2 mb-8"></div>
            <div className="h-64 bg-navy/10 rounded-xl mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-navy/10 rounded"></div>
              <div className="h-4 bg-navy/10 rounded w-5/6"></div>
              <div className="h-4 bg-navy/10 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-100 pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-serif text-3xl text-navy mb-4">{error}</h1>
            <button
              onClick={() => navigate('/blog')}
              className="liquid-glass-button px-6 py-3"
            >
              Wróć do bloga
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 pt-32 pb-20">
      <article className="max-w-4xl mx-auto px-6">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-navy/60 hover:text-gold transition-colors duration-300 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Wróć do bloga
          </Link>
        </motion.div>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          {/* Category */}
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-4 h-4 text-gold" />
            <span className="text-xs font-medium text-gold tracking-wider uppercase">
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-serif text-4xl md:text-5xl text-navy font-light leading-tight mb-6">
            {post.title}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-navy/60 font-light leading-relaxed mb-8">
            {post.excerpt}
          </p>

          {/* Meta */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-8 border-b border-gold/20">
            <div className="flex items-center gap-6 text-navy/50 text-sm">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 text-navy/50 hover:text-gold transition-colors duration-300 text-sm"
            >
              <Share2 className="w-4 h-4" />
              Udostępnij
            </button>
          </div>
        </motion.header>

        {/* Cover Image */}
        {post.cover_image && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12"
          >
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-auto rounded-2xl shadow-xl"
            />
          </motion.div>
        )}

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="liquid-glass-card p-8 md:p-12"
        >
          <div
            className="blog-content text-navy/80 font-light leading-relaxed"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </motion.div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12 flex flex-wrap gap-2"
          >
            {post.tags.map((tag, index) => (
              <span
                key={index}
                className="px-4 py-2 liquid-glass text-navy/60 text-sm rounded-full"
              >
                #{tag}
              </span>
            ))}
          </motion.div>
        )}

        {/* Back to Blog */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <Link
            to="/blog"
            className="liquid-glass-button px-8 py-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Wróć do wszystkich artykułów
          </Link>
        </motion.div>
      </article>
    </div>
  );
};

export default BlogPost;
